import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, NavLink } from "react-router-dom";
import { useAuth, API } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import {
  Search, Target, History as HistoryIcon, BookOpen, Settings, ArrowUpRight,
  Sparkles, BookMarked, Brain, LayoutDashboard,
} from "lucide-react";

const sidebarLink = ({ isActive }) =>
  `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-colors ${
    isActive ? "bg-[#E6F0E9] text-[#2C4C3B] font-medium" : "text-[#52606D] hover:bg-[#F4F5F0] hover:text-[#2C4C3B]"
  }`;

export default function Dashboard() {
  const { user, authHeaders } = useAuth();
  const [history, setHistory] = useState([]);
  const [goalHistory, setGoalHistory] = useState([]);
  const [stats, setStats] = useState({ activities: 0, disorders: 0 });

  useEffect(() => {
    (async () => {
      try {
        const [h, g, a, d] = await Promise.all([
          axios.get(`${API}/history`, { headers: authHeaders() }),
          axios.get(`${API}/goal-history`, { headers: authHeaders() }),
          axios.get(`${API}/activities`),
          axios.get(`${API}/disorders`),
        ]);
        setHistory(h.data.slice(0, 5));
        setGoalHistory(g.data.slice(0, 5));
        setStats({ activities: a.data.length, disorders: d.data.length });
      } catch {}
    })();
    // eslint-disable-next-line
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 py-10 grid lg:grid-cols-12 gap-8" data-testid="dashboard-page">
      {/* Sidebar */}
      <aside className="lg:col-span-3" data-testid="dashboard-sidebar">
        <div className="sticky top-24 p-4 rounded-2xl bg-white border border-[#E2E8F0]">
          <div className="px-2 py-1">
            <div className="text-xs tracking-[0.2em] uppercase text-[#52606D]">Workspace</div>
            <div className="font-display text-lg text-[#2C4C3B]">{user?.name}</div>
          </div>
          <nav className="mt-4 space-y-1">
            <NavLink to="/dashboard" end className={sidebarLink} data-testid="sb-dashboard">
              <LayoutDashboard className="w-4 h-4" /> Overview
            </NavLink>
            <NavLink to="/search" className={sidebarLink} data-testid="sb-programming">
              <Search className="w-4 h-4" /> Programming
            </NavLink>
            <NavLink to="/goals" className={sidebarLink} data-testid="sb-goals">
              <Target className="w-4 h-4" /> Goals
            </NavLink>
            <NavLink to="/history" className={sidebarLink} data-testid="sb-history">
              <HistoryIcon className="w-4 h-4" /> History
            </NavLink>
            <NavLink to="/clinical-notes" className={sidebarLink} data-testid="sb-notes">
              <BookOpen className="w-4 h-4" /> Clinical Notes
            </NavLink>
          </nav>
          <div className="mt-5 pt-4 border-t border-[#E2E8F0]">
            <Link to="/search">
              <Button className="w-full btn-primary rounded-full" data-testid="sb-quick-search">
                <Sparkles className="w-4 h-4 mr-2" /> New Session
              </Button>
            </Link>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="lg:col-span-9">
        <div>
          <span className="text-xs tracking-[0.2em] uppercase text-[#C66A51]">Dashboard</span>
          <h1 className="font-display text-4xl font-semibold text-[#2C4C3B] mt-1" data-testid="dashboard-greeting">
            Hello, {user?.name?.split(" ")[0]}.
          </h1>
          <p className="text-[#52606D] mt-2">Ready to programme the next session?</p>
        </div>

        <div className="mt-10 grid md:grid-cols-3 gap-5">
          <div className="p-6 rounded-2xl bg-white border border-[#E2E8F0] card-lift">
            <div className="flex items-center gap-2 text-[#52606D] text-xs tracking-[0.15em] uppercase">
              <BookMarked className="w-4 h-4" /> Activities
            </div>
            <div className="mt-3 font-display text-4xl text-[#2C4C3B]" data-testid="stat-activities">{stats.activities}</div>
            <p className="text-xs text-[#52606D] mt-1">Play + routines</p>
          </div>
          <div className="p-6 rounded-2xl bg-white border border-[#E2E8F0] card-lift">
            <div className="flex items-center gap-2 text-[#52606D] text-xs tracking-[0.15em] uppercase">
              <Brain className="w-4 h-4" /> Diagnosis profiles
            </div>
            <div className="mt-3 font-display text-4xl text-[#2C4C3B]" data-testid="stat-disorders">{stats.disorders}</div>
            <p className="text-xs text-[#52606D] mt-1">Paediatric & adult</p>
          </div>
          <div className="p-6 rounded-2xl bg-white border border-[#E2E8F0] card-lift">
            <div className="flex items-center gap-2 text-[#52606D] text-xs tracking-[0.15em] uppercase">
              <HistoryIcon className="w-4 h-4" /> Recent outputs
            </div>
            <div className="mt-3 font-display text-4xl text-[#2C4C3B]" data-testid="stat-searches">{history.length + goalHistory.length}</div>
            <p className="text-xs text-[#52606D] mt-1">Programming + goals</p>
          </div>
        </div>

        <section className="mt-12">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display text-2xl text-[#2C4C3B]">Recent programming</h2>
            <Link to="/history" className="text-sm text-[#2C4C3B] hover:underline" data-testid="dashboard-view-all-history">View all →</Link>
          </div>
          {history.length === 0 ? (
            <div className="p-10 rounded-2xl border border-dashed border-[#E2E8F0] bg-white text-center text-[#52606D]" data-testid="dashboard-empty-history">
              No programming sessions yet.
              <div className="mt-4">
                <Link to="/search"><Button className="btn-primary rounded-full" data-testid="dashboard-empty-cta">Begin Programming</Button></Link>
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {history.map((h) => (
                <Link to={`/results/${h.id}`} key={h.id}
                  className="p-5 rounded-2xl bg-white border border-[#E2E8F0] card-lift flex items-center justify-between"
                  data-testid={`history-card-${h.id}`}>
                  <div>
                    <div className="text-xs tracking-[0.15em] uppercase text-[#C66A51]">{h.communicator_stage_title}</div>
                    <div className="font-display text-lg text-[#2C4C3B] mt-1">{h.activity_name}</div>
                    <div className="text-xs text-[#52606D] mt-1">{h.disorder_name} · {h.severity_label}</div>
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-[#2C4C3B]" />
                </Link>
              ))}
            </div>
          )}
        </section>

        <section className="mt-12">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display text-2xl text-[#2C4C3B]">Recent goal banks</h2>
            <Link to="/goals" className="text-sm text-[#2C4C3B] hover:underline" data-testid="dashboard-view-all-goals">New goal bank →</Link>
          </div>
          {goalHistory.length === 0 ? (
            <div className="p-10 rounded-2xl border border-dashed border-[#E2E8F0] bg-white text-center text-[#52606D]" data-testid="dashboard-empty-goals">
              No goal banks generated yet.
              <div className="mt-4">
                <Link to="/goals"><Button className="btn-primary rounded-full" data-testid="dashboard-empty-goals-cta">Build First Goal Bank</Button></Link>
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {goalHistory.map((g) => (
                <Link to={`/goal-results/${g.id}`} key={g.id}
                  className="p-5 rounded-2xl bg-white border border-[#E2E8F0] card-lift flex items-center justify-between"
                  data-testid={`goal-history-card-${g.id}`}>
                  <div>
                    <div className="text-xs tracking-[0.15em] uppercase text-[#C66A51]">{g.goals.length} goals · {g.communicator_stage_title}</div>
                    <div className="font-display text-lg text-[#2C4C3B] mt-1">{g.activity_name}</div>
                    <div className="text-xs text-[#52606D] mt-1">{g.disorder_name} · {g.severity_label}</div>
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-[#2C4C3B]" />
                </Link>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
